import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

interface StaggerEntranceOptions {
  /** CSS selector for children to animate within the container */
  selector?: string
  stagger?: number
  duration?: number
  y?: number
  delay?: number
}

/**
 * Staggered entrance animation for a list of cards/items.
 * Usage: attach the returned `ref` to the container element.
 * Children matching `selector` fade+slide in on mount.
 *
 * Uses `useGSAP()` — never `useEffect` — per GSAP React best practices.
 */
export function useStaggerEntrance<T extends HTMLElement = HTMLDivElement>({
  selector = ':scope > *',
  stagger = 0.05,
  duration = 0.4,
  y = 10,
  delay = 0,
}: StaggerEntranceOptions = {}) {
  const ref = useRef<T>(null)

  useGSAP(
    () => {
      if (!ref.current) return
      const targets = ref.current.querySelectorAll(selector)
      if (!targets.length) return

      gsap.fromTo(
        targets,
        { opacity: 0, y },
        {
          opacity: 1,
          y: 0,
          stagger,
          duration,
          delay,
          ease: 'power2.out',
          clearProps: 'transform',
        },
      )
    },
    { scope: ref },
  )

  return ref
}
